
import React from 'react';
import { AppContext } from '../context/AppContext';
import { useAllOrders } from '../context/AllOrdersContext';
import { useData } from '../context/DataContext';
import { formatDateReadable } from '../utils/dateUtils';


const LivreurActiveOrderScreen: React.FC = () => {
    const { view, setView } = React.useContext(AppContext);
    const { placedOrders, updateOrderStatus } = useAllOrders();
    const { restaurants } = useData();

    const orderId = view.params?.orderId;
    const order = placedOrders.find(o => o.id === orderId);

    if (!order) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center bg-white">
                <div className="w-20 h-20 bg-gray-50 rounded-[2rem] flex items-center justify-center mb-8 shadow-inner border border-gray-100">
                    <span className="text-4xl grayscale opacity-20">❓</span>
                </div>
                <h3 className="text-xl font-black text-gray-900 tracking-tighter uppercase italic mb-2">Oups !</h3>
                <p className="text-gray-400 font-bold text-[10px] uppercase tracking-widest italic mb-8">Mission introuvable ou archivée</p>
                <button
                    onClick={() => setView({ screen: 'livreurHome' })}
                    className="px-8 py-4 bg-black text-white font-black rounded-2xl text-[10px] uppercase italic tracking-[0.15em] active:scale-95 transition-all shadow-xl shadow-gray-200"
                >
                    Retour aux missions
                </button>
            </div>
        );
    }

    const restaurant = restaurants.find(r => r.id === order.restaurantId);

    const openMap = (lat?: number, lng?: number, query?: string) => {
        if (lat && lng) {
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
        } else if (query) {
            window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`, '_blank');
        } else {
            alert("Localisation non disponible.");
        }
    };

    const handleComplete = async () => {
        if (window.confirm('Voulez-vous marquer cette commande comme déposée ?')) {
            await updateOrderStatus(order.id, 'Livrée');
            setView({ screen: 'livreurHome' });
        }
    };

    const isStep1 = ['En attente', 'En préparation', 'Prête'].includes(order.status);

    return (
        <div className="min-h-screen bg-white pb-24">
            {/* Premium Header */}
            <div className="bg-white px-6 pt-8 pb-4 sticky top-0 z-20 flex items-center justify-between border-b border-gray-50 backdrop-blur-md bg-white/90">
                <button
                    onClick={() => setView({ screen: 'livreurHome' })}
                    className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-2xl text-gray-900 shadow-sm border border-gray-100 active:scale-90 transition-all"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <h1 className="text-sm font-black text-gray-900 tracking-[0.2em] uppercase italic text-center">
                    Mission <span className="text-red-600">En cours</span>
                </h1>
                <div className="w-12" /> {/* Balancing spacer */}
            </div>

            <div className="p-6 space-y-8">
                {/* Visual Timeline - Editorial Style */}
                <div className="relative px-4">
                    <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-100 -translate-y-1/2 -z-0" />
                    <div className="flex justify-between relative z-10">
                        <div className="flex flex-col items-center">
                            <div className={`w-14 h-14 rounded-full flex items-center justify-center font-black italic shadow-2xl transition-all ${isStep1 ? 'bg-black text-white scale-110 shadow-gray-300' : 'bg-green-500 text-white shadow-green-200'}`}>
                                {isStep1 ? '01' : '✓'}
                            </div>
                            <span className={`text-[9px] font-black uppercase tracking-widest mt-4 italic ${isStep1 ? 'text-black' : 'text-green-500'}`}>Check-in</span>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className={`w-14 h-14 rounded-full flex items-center justify-center font-black italic shadow-2xl transition-all ${order.status === 'Envoyée' ? 'bg-black text-white scale-110 shadow-gray-300' : isStep1 ? 'bg-gray-50 text-gray-200 border border-gray-100' : 'bg-gray-50 text-gray-400'}`}>
                                02
                            </div>
                            <span className={`text-[9px] font-black uppercase tracking-widest mt-4 italic ${order.status === 'Envoyée' ? 'text-black' : 'text-gray-300'}`}>Livraison</span>
                        </div>
                    </div>
                </div>

                {/* Main Action Area */}
                <section className="animate-fade-in-up">
                    {isStep1 ? (
                        <div className="bg-white p-8 rounded-[3rem] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.12)] border border-gray-50 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-red-600/5 rounded-full -mr-16 -mt-16" />
                            
                            <div className="flex items-center gap-4 mb-8">
                                <div className="w-16 h-16 bg-black text-white rounded-[1.5rem] flex items-center justify-center text-3xl shadow-xl">🏬</div>
                                <div>
                                    <h2 className="text-2xl font-black text-gray-900 leading-tight tracking-tighter uppercase italic">Collecte</h2>
                                    <p className="text-[10px] text-red-600 font-black uppercase tracking-widest italic flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 bg-red-600 rounded-full animate-pulse" /> Étape 01/02
                                    </p>
                                </div>
                            </div>

                            <div className="bg-gray-50/50 p-6 rounded-[2rem] border border-gray-100 mb-8">
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest italic mb-2">Restaurant</p>
                                <p className="font-black text-gray-900 text-xl uppercase tracking-tighter italic mb-1">{restaurant?.name || order.items[0]?.restaurantName}</p>
                                <p className="text-xs text-gray-500 font-bold leading-relaxed uppercase italic opacity-70">
                                    {restaurant?.address || restaurant?.wilaya || 'Alger, Algérie'}
                                </p>
                            </div>

                            <button
                                onClick={() => openMap(restaurant?.location?.lat, restaurant?.location?.lng, restaurant?.address || restaurant?.name)}
                                className="w-full py-5 bg-black text-white font-black rounded-[2rem] flex items-center justify-center gap-3 shadow-2xl shadow-gray-200 active:scale-95 transition-all text-xs uppercase italic tracking-[0.2em]"
                            >
                                <span className="text-lg">📍</span>
                                Ouvrir l'itinéraire
                            </button>

                            <div className="mt-8 pt-6 border-t border-dashed border-gray-100 text-center">
                                <p className="text-[9px] text-gray-400 font-black uppercase tracking-widest italic opacity-50">
                                    Attente validation restaurant
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white p-8 rounded-[3rem] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.12)] border border-gray-50 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/5 rounded-full -mr-16 -mt-16" />
                            
                            <div className="flex items-center gap-4 mb-8">
                                <div className="w-16 h-16 bg-black text-white rounded-[1.5rem] flex items-center justify-center text-3xl shadow-xl">🏡</div>
                                <div>
                                    <h2 className="text-2xl font-black text-gray-900 leading-tight tracking-tighter uppercase italic">Livraison</h2>
                                    <p className="text-[10px] text-green-600 font-black uppercase tracking-widest italic flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 bg-green-600 rounded-full animate-pulse" /> Étape 02/02
                                    </p>
                                </div>
                            </div>

                            <div className="bg-gray-50/50 p-6 rounded-[2rem] border border-gray-100 mb-6">
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest italic mb-2">Destinataire</p>
                                <p className="font-black text-gray-900 text-xl uppercase tracking-tighter italic mb-1">{order.customerName}</p>
                                <p className="text-xs text-gray-500 font-bold leading-relaxed uppercase italic opacity-70">
                                    {order.customerAddress || 'Adresse client non spécifiée'}
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-8">
                                <a
                                    href={`tel:${order.customerPhone}`}
                                    className="py-5 bg-gray-100 text-black font-black rounded-[2rem] flex items-center justify-center gap-3 active:scale-95 transition-all text-[10px] uppercase italic tracking-widest border border-gray-200"
                                >
                                    📞 Appeler
                                </a>
                                <button
                                    onClick={() => {
                                        const loc = order.customerLocation as any;
                                        const lat = loc?.lat ?? loc?.latitude;
                                        const lng = loc?.lng ?? loc?.longitude;
                                        openMap(lat, lng, order.customerAddress || order.customerName);
                                    }}
                                    className="py-5 bg-black text-white font-black rounded-[2rem] flex items-center justify-center gap-3 shadow-2xl shadow-gray-200 active:scale-95 transition-all text-[10px] uppercase italic tracking-widest"
                                >
                                    📍 Itinéraire
                                </button>
                            </div>

                            <div className="bg-black text-white p-7 rounded-[2.5rem] shadow-2xl relative overflow-hidden mb-8 group">
                                <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-8 -mt-8 group-hover:scale-110 transition-transform" />
                                <div className="flex justify-between items-end relative z-10">
                                    <div>
                                        <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest italic mb-2">Total à encaisser</p>
                                        <p className="text-3xl font-black italic tracking-tighter leading-none">{order.totalPrice} <span className="text-xs">DA</span></p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest italic mb-2">Votre Gain</p>
                                        <p className="text-xl font-black text-green-400 italic tracking-tighter leading-none">+{order.deliveryFee} DA</p>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleComplete}
                                className="w-full py-6 bg-red-600 text-white font-black rounded-[2rem] active:scale-[0.98] transition-all text-[11px] uppercase italic tracking-[0.25em] shadow-xl shadow-red-100 flex items-center justify-center gap-3"
                            >
                                {order.status === 'Livrée' ? 'MISSION TERMINÉE ✓' : 'Confirmer le Dépôt'}
                            </button>
                        </div>
                    )}
                </section>

                {/* Secondary Info Area */}
                <div className="bg-gray-50/50 p-8 rounded-[3rem] border border-gray-100">
                    <div className="flex justify-between items-baseline mb-6">
                        <h3 className="text-[10px] font-black text-gray-400 tracking-[0.2em] uppercase italic">Détails Commande</h3>
                        <span className="text-[9px] text-gray-300 font-bold uppercase">{formatDateReadable(order.timestamp)}</span>
                    </div>
                    <ul className="space-y-5">
                        {order.items.map((item: any, idx) => {
                            const isPack = item.type === 'pack';
                            const itemName = isPack ? (item.pack?.name || 'Pack') : (item.dish?.name || 'Plat');
                            return (
                                <li key={idx} className="flex flex-col border-b border-gray-200/50 last:border-0 pb-3 last:pb-0">
                                    <div className="flex justify-between items-baseline">
                                        <p className="text-sm font-black text-gray-900 uppercase tracking-tighter italic leading-tight">
                                            <span className="text-red-600 mr-2">{item.quantity}x</span> {itemName}
                                        </p>
                                    </div>
                                    {isPack && item.pack?.dishes && (
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {item.pack.dishes.map((d: any, dIdx: number) => (
                                                <span key={dIdx} className="text-[8px] font-black text-gray-400 bg-white border border-gray-100 px-2 py-0.5 rounded-full uppercase italic tracking-tighter">
                                                    {d.quantity && d.quantity > 1 ? `${d.quantity}x ` : ''}{d.name}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default LivreurActiveOrderScreen;
